import React, { useState, useEffect, useRef } from 'react';
import { downloadCsv, userRidesCsvColumns } from '../common/sharedFunctions';
import MaterialTable from 'material-table';
import { useSelector } from 'react-redux';
import CircularLoading from '../components/CircularLoading';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment/min/moment-with-locales';
import { SECONDORY_COLOR } from '../common/sharedFunctions';
import { colors } from '../components/Theme/WebTheme';
import { ThemeProvider } from '@mui/material/styles';
import theme from 'styles/tableStyle';
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import GoBackButton from 'components/GoBackButton';
import { Grid, Typography, Button, TextField, Box } from '@mui/material';

export default function UserRides({ data, tabid }) {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const bookinglistdata = useSelector(state => state.bookinglistdata);
  const settings = useSelector(state => state.settingsdata.settings);
  const loaded = useRef(false);
  const [bookingData, setBookingData] = useState([]);
  const navigate = useNavigate();
  const [selectedRow, setSelectedRow] = useState(null);
  const { state } = useLocation();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [filteredBookingData, setFilteredBookingData] = useState([]);

  useEffect(() => {
    if (bookinglistdata.bookings) {
      let filtered = [];
      if (data.usertype === 'customer') {
        filtered = bookinglistdata.bookings.filter(
          item => item.customer === id
        );
      } else if (data.usertype === 'driver') {
        filtered = bookinglistdata.bookings.filter(item => item.driver === id);
      }

      // Apply date filters
      if (fromDate) {
        const fromTimestamp = new Date(fromDate).getTime();
        filtered = filtered.filter(
          booking => booking.bookingDate >= fromTimestamp
        );
      }

      if (toDate) {
        const toTimestamp = new Date(toDate + 'T23:59:59').getTime();
        filtered = filtered.filter(
          booking => booking.bookingDate <= toTimestamp
        );
      }

      setBookingData(filtered);
      setFilteredBookingData(filtered);
    } else {
      setBookingData([]);
      setFilteredBookingData([]);
    }
    loaded.current = true;
  }, [bookinglistdata.bookings, id, data, fromDate, toDate]);

  const col = [
    {
      title: t('booking_status'),
      field: 'status',
      render: rowData => (
        <div
          style={{
            backgroundColor:
              rowData.status === 'CANCELLED'
                ? colors.RED
                : rowData.status === 'COMPLETE'
                  ? colors.GREEN
                  : colors.YELLOW,
            color: 'white',
            padding: 7,
            borderRadius: '15px',
            fontWeight: 'bold',
            width: '150px',
            margin: 'auto',
          }}
        >
          {t(rowData.status)}
        </div>
      ),
    },
    {
      title: t('booking_ref'),
      field: 'reference',
    },
    {
      title: t('booking_date'),
      field: 'bookingDate',
      render: rowData =>
        rowData.bookingDate ? moment(rowData.bookingDate).format('lll') : null,
    },
    {
      title: t('trip_start_time'),
      field: 'trip_start_time',
    },
    {
      title: t('trip_end_time'),
      field: 'trip_end_time',
    },
    {
      title: t('customer_name'),
      field: 'customer_name',
    },
    {
      title: t('customer_contact'),
      field: 'customer_contact',
    },
    {
      title: t('pickup_address'),
      field: 'pickupAddress',
    },
    {
      title: t('drop_address'),
      field: 'dropAddress',
    },
    {
      title: t('deliveryPerson'),
      field: 'deliveryPerson',
    },
    {
      title: t('deliveryPersonPhone'),
      field: 'deliveryPersonPhone',
    },
    {
      title: t('distance_web'),
      field: 'distance',
    },
    {
      title: t('trip_cost'),
      field: 'trip_cost',
    },
  ];

  return !loaded.current ? (
    <CircularLoading />
  ) : (
    <>
      <div
        style={{
          backgroundColor: colors.LandingPage_Background,
          borderRadius: 10,
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <GoBackButton
          isRTL={isRTL}
          onClick={() => {
            navigate(`/users/${tabid}`, { state: { pageNo: state?.pageNo } });
          }}
        />

        {/* Date Filter Section */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: 'white',
            borderRadius: 1,
            boxShadow: 1,
            mx: 2,
            mt: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontFamily: 'inherit' }}>
            Filter by Date
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="From Date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="To Date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                onClick={() => {
                  setFromDate('');
                  setToDate('');
                }}
                sx={{ height: 56 }}
              >
                Clear Filters
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Showing: {filteredBookingData.length} of{' '}
                {bookingData?.length || 0} Rides
              </Typography>
            </Grid>
          </Grid>
        </Box>
        <ThemeProvider theme={theme}>
          <MaterialTable
            title={t('bookings_table_title')}
            columns={col}
            style={{
              direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
              borderRadius: '8px',
              boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
              width: '100%',
            }}
            data={bookingData}
            onRowClick={(evt, selectedRow) =>
              setSelectedRow(selectedRow.tableData.id)
            }
            options={{
              exportCsv: (columns, data) => {
                // Use the CSV-specific columns for export
                const csvColumns = userRidesCsvColumns(t);
                let hArray = [];
                const headerRow = csvColumns.map(col => {
                  hArray.push(col.field);
                  return col.title;
                });
                const dataRows = data.map(({ tableData, ...row }) => {
                  // Handle booking ID
                  row.id = row.id || '';

                  // Handle booking reference
                  row.reference = row.reference || '';

                  // Format booking date
                  row.bookingDate =
                    new Date(row.bookingDate).toLocaleDateString() +
                    ' ' +
                    new Date(row.bookingDate).toLocaleTimeString();

                  // Format trip start time
                  row.trip_start_time = row.trip_start_time || '';

                  // Format trip end time
                  row.trip_end_time = row.trip_end_time || '';

                  // Handle vehicle type
                  row.carType = row.carType || '';

                  // Handle customer name
                  row.customer_name = row.customer_name || '';

                  // Handle addresses - replace commas with semicolons
                  row.pickupAddress = (row.pickupAddress || '').replace(
                    /,/g,
                    ';'
                  );
                  row.dropAddress = (row.dropAddress || '').replace(/,/g, ';');

                  // Handle assign driver
                  row.driver_name = row.driver_name || '';

                  // Handle delivery person details
                  row.deliveryPerson = row.deliveryPerson || '';
                  row.deliveryPersonPhone = row.deliveryPersonPhone || '';

                  // Handle instructions - replace commas with semicolons
                  row.pickUpInstructions = (
                    row.pickUpInstructions || ''
                  ).replace(/,/g, ';');
                  row.tripInstructions = (row.tripInstructions || '').replace(
                    /,/g,
                    ';'
                  );

                  // Handle booking status
                  row.status = row.status || '';

                  // Handle cancellation reason
                  row.reason = row.reason || '';

                  // Handle trip cost
                  row.trip_cost = row.trip_cost || '';

                  // Handle distance
                  row.distance = row.distance || '';

                  // Handle vehicle number
                  row.vehicle_number = row.vehicle_number || '';

                  let dArr = [];
                  for (let i = 0; i < hArray.length; i++) {
                    dArr.push(row[hArray[i]]);
                  }
                  return Object.values(dArr);
                });
                const { exportDelimiter } = ',';
                const delimiter = exportDelimiter ? exportDelimiter : ',';
                const csvContent = [headerRow, ...dataRows]
                  .map(e => e.join(delimiter))
                  .join('\n');

                // Create filename with date range if filters are applied
                let csvFileName = 'user_rides.csv';
                if (fromDate || toDate) {
                  const fromStr = fromDate ? fromDate.replace(/-/g, '') : 'all';
                  const toStr = toDate ? toDate.replace(/-/g, '') : 'all';
                  csvFileName = `user_rides_${fromStr}_to_${toStr}.csv`;
                }

                downloadCsv(csvContent, csvFileName);
              },
              exportButton: {
                csv: settings.AllowCriticalEditsAdmin,
                pdf: false,
              },
              maxColumnSort: 'all_columns',
              rowStyle: rowData => ({
                backgroundColor:
                  selectedRow === rowData.tableData.id
                    ? colors.THIRDCOLOR
                    : colors.WHITE,
              }),
              ...TableStyle(),
            }}
            localization={localization(t)}
          />
        </ThemeProvider>
      </div>
    </>
  );
}
